import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { PROSPECT_STATUSES, FOLLOWUP_KINDS, courseById, type ProspectStatus } from "@mizpah/shared";
import { api, AdminError } from "../adminClient.js";
import { badgeClass } from "../badge.js";

interface Prospect {
  prospectId: string; fullName: string; email: string; phone?: string; courseId: string;
  preferredBatchMonth?: string; status: ProspectStatus; emailStatus?: string; source?: string;
  mailingAddress?: string; howHeard?: string; comments?: string;
}
interface Followup { loggedAt: string; kind: string; note?: string; by?: string; }
interface BatchOption {
  batchId: string; courseId: string; name: string; capacity: number; enrolledCount: number;
}

export default function LeadDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [followups, setFollowups] = useState<Followup[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState("");
  const [fkind, setFkind] = useState<string>(FOLLOWUP_KINDS[0]);
  const [fnote, setFnote] = useState("");
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [batches, setBatches] = useState<BatchOption[] | null>(null);
  const [batchId, setBatchId] = useState("");

  const load = useCallback(() => {
    api<{ prospect: Prospect; followups: Followup[] }>(`/admin/inquiries/${id}`)
      .then((d) => { setProspect(d.prospect); setFollowups(d.followups); })
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load"));
  }, [id]);
  useEffect(load, [load]);

  async function act(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setError(""); setSaved("");
    try {
      await fn(); load();
      setSaved(label); window.setTimeout(() => setSaved((s) => (s === label ? "" : s)), 2500);
    }
    catch (e) { setError(e instanceof AdminError ? e.message : "Action failed"); }
    finally { setBusy(""); }
  }

  function openEnroll() {
    setEnrollOpen(true); setError("");
    if (batches) return;
    api<{ items: BatchOption[] }>("/admin/batches")
      .then((d) => {
        const forCourse = d.items.filter((b) => b.courseId === prospect?.courseId);
        setBatches(forCourse);
        const firstOpen = forCourse.find((b) => b.enrolledCount < b.capacity);
        if (firstOpen) setBatchId(firstOpen.batchId);
      })
      .catch((e) => setError(e instanceof AdminError ? e.message : "Failed to load batches"));
  }

  async function enroll() {
    if (!batchId) return;
    setBusy("enroll"); setError("");
    try {
      const res = await api<{ student: { studentId: string } }>(`/admin/inquiries/${id}/enroll`, { method: "POST", body: { batchId } });
      nav(`/admin/students/${res.student.studentId}`);
    } catch (e) {
      // Already enrolled (idempotency): jump to the existing student record.
      if (e instanceof AdminError && e.status === 409 && e.fields?.studentId) {
        nav(`/admin/students/${e.fields.studentId}`);
        return;
      }
      setError(e instanceof AdminError ? e.message : "Enrollment failed");
    } finally {
      setBusy("");
    }
  }

  if (error && !prospect) return <div className="login-err">{error}</div>;
  if (!prospect) return <p className="subhead">Loading...</p>;

  const badEmail = prospect.emailStatus === "bounced" || prospect.emailStatus === "complained" || prospect.emailStatus === "failed";
  const canEnroll = prospect.status !== "Enrolled" && prospect.status !== "Dropped";

  return (
    <>
      <p className="subhead"><Link to="/admin">← All inquiries</Link></p>
      {error && <div className="login-err" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="detail">
        <div className="card pad">
          <div className="row">
            <h1 style={{ fontSize: 22 }}>{prospect.fullName}</h1>
            <span className={badgeClass(prospect.status)}>{prospect.status}</span>
          </div>
          <dl className="kv">
            <dt>Email</dt><dd>{prospect.email} {prospect.emailStatus && <span className={`flag`} style={{ marginLeft: 6 }}>email {prospect.emailStatus}</span>}</dd>
            <dt>Phone</dt><dd>{prospect.phone || "-"}</dd>
            <dt>Mailing address</dt><dd>{prospect.mailingAddress || "-"}</dd>
            <dt>Course</dt><dd>{courseById(prospect.courseId)?.name ?? prospect.courseId}</dd>
            <dt>Preferred start</dt><dd>{prospect.preferredBatchMonth || "-"}</dd>
            <dt>How heard</dt><dd>{prospect.howHeard || "-"}</dd>
            <dt>Source</dt><dd>{prospect.source || "-"}</dd>
            {prospect.comments && <><dt>Comments</dt><dd>{prospect.comments}</dd></>}
          </dl>
        </div>

        <div className="card pad">
          {canEnroll && (
            <div style={{ marginBottom: 16 }}>
              {!enrollOpen ? (
                <button className="rbtn coral" disabled={!!busy} onClick={openEnroll}>Enroll into a batch</button>
              ) : (
                <>
                  <div className="alabel" style={{ marginTop: 0 }}>Enroll into a batch</div>
                  {batches === null ? (
                    <p className="sub">Loading batches...</p>
                  ) : batches.length === 0 ? (
                    <p className="sub">No {courseById(prospect.courseId)?.name ?? "matching"} batches yet. Create one on the Batches screen.</p>
                  ) : (
                    <>
                      <select className="ainput" aria-label="Batch to enroll into" value={batchId} onChange={(e) => setBatchId(e.target.value)}>
                        {batches.map((b) => {
                          const remaining = b.capacity - b.enrolledCount;
                          return <option key={b.batchId} value={b.batchId} disabled={remaining <= 0}>{b.name} ({remaining > 0 ? `${remaining} seats left` : "full"})</option>;
                        })}
                      </select>
                      <div className="actions" style={{ marginTop: 12 }}>
                        <button className="rbtn coral" disabled={!!busy || !batchId} onClick={enroll}>{busy === "enroll" ? "Enrolling..." : "Confirm enrollment"}</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div className="alabel" style={{ marginTop: 0 }}>Follow-up activity</div>
          <ul className="tl">
            {followups.length === 0 && <li>No activity yet.</li>}
            {followups.map((f, i) => (
              <li key={i}><strong>{labelFor(f.kind)}</strong>{f.note ? ` - ${f.note}` : ""}<div className="t">{new Date(f.loggedAt).toLocaleString()}{f.by ? ` · ${f.by}` : ""}</div></li>
            ))}
          </ul>

          <div className="alabel">Status
            {busy === "status" && <span className="sub"> · Saving...</span>}
            {saved === "status" && <span className="sub savedcue"> · Saved ✓</span>}
          </div>
          <select className="ainput" aria-label="Lead status" value={prospect.status} disabled={!!busy}
            onChange={(e) => act("status", () => api(`/admin/inquiries/${id}/status`, { method: "PATCH", body: { status: e.target.value } }))}>
            {/* "Enrolled" is not hand-pickable - enrolling into a batch sets it.
                It's still rendered (disabled) when it IS the current status, so the
                select has a matching option and doesn't show blank. */}
            {PROSPECT_STATUSES
              .filter((s) => s !== "Enrolled" || prospect.status === "Enrolled")
              .map((s) => (
                <option key={s} value={s} disabled={s === "Enrolled"}>{s}</option>
              ))}
          </select>
          {prospect.status !== "Enrolled" && (
            <p className="sub" style={{ marginTop: 6 }}>
              To enroll, use <strong>Enroll into a batch</strong> above - the status updates automatically.
            </p>
          )}

          <div className="alabel">Log a follow-up</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select className="ainput" aria-label="Follow-up type" style={{ flex: "0 0 130px" }} value={fkind} onChange={(e) => setFkind(e.target.value)}>
              {FOLLOWUP_KINDS.map((k) => <option key={k} value={k}>{labelFor(k)}</option>)}
            </select>
            <input className="ainput" aria-label="Follow-up note" style={{ flex: 1, minWidth: 120 }} placeholder="Note (optional)" value={fnote} onChange={(e) => setFnote(e.target.value)} />
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button className="rbtn" disabled={!!busy} onClick={() => act("log", async () => { await api(`/admin/inquiries/${id}/followup`, { method: "POST", body: { kind: fkind, note: fnote } }); setFnote(""); })}>Log it</button>
            <button className="rbtn coral" disabled={!!busy} onClick={() => act("reminder", () => api(`/admin/inquiries/${id}/reminder`, { method: "POST" }))}>{busy === "reminder" ? "Sending..." : "Send follow-up reminder"}</button>
            {badEmail && <button className="rbtn warn" disabled={!!busy} onClick={() => act("resend", () => api(`/admin/inquiries/${id}/resend`, { method: "POST" }))}>{busy === "resend" ? "Resending..." : "Resend auto-email"}</button>}
          </div>
        </div>
      </div>
    </>
  );
}

function labelFor(kind: string): string {
  return ({ called: "Called", texted: "Texted", emailed: "Emailed", reminder_sent: "Reminder sent", note: "Note" } as Record<string, string>)[kind] ?? kind;
}
